import { QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";

import { ServerError } from "./types";

import { getQueryKey } from "@/helpers";

export type UseGetOptions<TQueryFnData, TData = TQueryFnData, TError = AxiosError<ServerError>> = Partial<
  Omit<UseQueryOptions<TQueryFnData, TError, TData>, "queryKey"> & {
    onSuccess: (_data: TData) => void;
    onError: (_error: TError) => void;
  }
>;

type UseGetProps<TQueryFnData, TData, TError> = {
  endpoint: string;
  options: UseGetOptions<TQueryFnData, TData, TError>;
  queryKey?: QueryKey;
};

export function useGet<TQueryFnData, TData = TQueryFnData, TError = AxiosError<ServerError>>({
  endpoint,
  queryKey = getQueryKey({ endpoint }),
  options,
}: UseGetProps<TQueryFnData, TData, TError>) {
  const query = useQuery<TQueryFnData, TError, TData>({
    ...options,
    queryKey,
    queryFn: async () => {
      const data = (await axios.get(endpoint)).data;
      if (options?.onSuccess) {
        options.onSuccess(data as TData);
      }
      return data;
    },
  });

  const { error, isFetching, isError } = query;

  if (isError && !isFetching && options?.onError) {
    options.onError(error);
  }

  return query;
}
